<script lang="ts">
	import { format } from 'svelte-i18n';
	import { getContext, onDestroy, onMount } from 'svelte';

	import type { Writable } from 'svelte/store';
	import type { ApplianceType } from '../../../../helpers/device_settings_helpers';
	import type { Device } from '../../../../types/Device';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';

	import { devices } from 'src/stores/lists/devices';
	import { home_data } from 'src/stores/account/user';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	import EnergyCategoryDesignation from './EnergyCategoryDesignation.svelte';
	import EnergyCategoryTypePicker from './EnergyCategoryTypePicker.svelte';

	import { Button, ButtonV2, Modal, Text, toast } from '@Shelly/shelly-design-system';

	import {
		onSettingsResponse,
		getEnergyCategoryTypes,
		castUsageStringToRelayUsageParam,
	} from '../../../../helpers/device_settings_helpers';
	import { requestEditDevice } from 'src/components/Lists/list-helpers';
	import { postReq } from 'src/services/http';
	import {
		getHomeEnergyDesignationForDevice,
		isDeviceEnergyDesignated,
		isSourceRelayUsage,
	} from 'src/components/Settings/HomeManagement/shared/solar-setup-helpers';
	import { removeDeviceFromHomeEnergyDesignations } from 'src/components/Settings/HomeManagement/shared/solar-setup-persist';

	export let device: Device;

	const types: ApplianceType[] = getEnergyCategoryTypes();

	$: designation = getHomeEnergyDesignationForDevice(device, $home_data);
	$: isDesignated = isDeviceEnergyDesignated(designation);

	let selected: ApplianceType | undefined = types.find((type) => type.usage === device.relay_usage);
	let waitForResponse = false;
	let initialSave = false;
	let savedUsage = device.relay_usage;
	let showClearDesignationModal = false;

	$: isSaveDisabled = waitForResponse || !selected || selected?.usage === savedUsage;

	const persist = async (): Promise<void> => {
		if (!selected || device.relay_usage === selected.usage) {
			if (!$showNewLayout) {
				toast.open({
					type: 'info',
					title: $format('toast_select_different_appliance_type', {
						default: 'Please select a different appliance type to change it.',
					}),
				});
			}
			return;
		}

		waitForResponse = true;

		device.appliance_type = selected?.value;
		device.relay_usage = castUsageStringToRelayUsageParam(selected);
		const res = await requestEditDevice(device);

		if (res.isok) {
			savedUsage = device.relay_usage;
			if (device.relay_usage !== 'light') {
				const internalRes = await postReq('interface/device/set_cloud_options', {
					id: device.id,
					set: 'exclude_forgotten_lights',
					val: true,
				});
				if (internalRes.isok) {
					device.cloud_options.exclude_forgotten_lights = true;
					devices.update(device);
				}
			}
		}

		if (!initialSave) {
			onSettingsResponse(res, device, {
				success: selected?.title
					? $format('appliance_type_scc_toast', {
							values: { value: selected?.title },
							default: `Appliance type was changed to ${selected?.title}.`,
						})
					: $format('appliance_type_disabled_scc_toast', {
							default: 'Appliance type was disabled.',
						}),
			});
		} else {
			initialSave = false;
		}
		waitForResponse = false;
	};

	const save = async (): Promise<void> => {
		if (isDesignated && selected && selected.usage !== savedUsage) {
			showClearDesignationModal = true;
			return;
		}
		await persist();
	};

	const handleClearDesignationConfirm = async (): Promise<void> => {
		waitForResponse = true;
		const removed = await removeDeviceFromHomeEnergyDesignations(device.id);
		if (!removed) {
			waitForResponse = false;
			toast.open({
				type: 'danger',
				title: $format('error_global', { default: 'Something went wrong!' }),
			});
			return;
		}
		showClearDesignationModal = false;
		waitForResponse = false;
		await persist();
	};

	const selectType = (type: ApplianceType): void => {
		selected = type;
	};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({
				onClick: save,
				loading: waitForResponse,
				disabled: isSaveDisabled,
				icon: 'fas fa-save',
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});

	onMount(() => {
		if (
			typeof selected?.usage === 'undefined' &&
			!isSourceRelayUsage(device.relay_usage) &&
			!isDesignated
		) {
			selected = types.filter((at) => at.usage == 'other')[0];
			initialSave = true;
			save();
		}
	});
</script>

<EnergyCategoryTypePicker selectedUsage={selected?.usage} on:select={(e) => selectType(e.detail)}>
	<EnergyCategoryDesignation {designation} />
</EnergyCategoryTypePicker>

{#if !$showNewLayout}
	<Button size="m" width="full" loading={waitForResponse} on:click={save}
		>{$format('button_save', { default: 'save' })}</Button
	>
{/if}

<Modal
	showModal={showClearDesignationModal}
	title={$format('confirm_clear_energy_designation_title', {
		default: 'Change energy category?',
	})}
	backdropClose={!waitForResponse}
	on:close={() => {
		if (!waitForResponse) showClearDesignationModal = false;
	}}
>
	<svelte:fragment slot="content">
		<Text styleType="body">
			{$format('confirm_clear_energy_designation_message', {
				default:
					'This device is designated as a home energy meter. Changing its energy category will remove that designation.',
			})}
		</Text>
	</svelte:fragment>
	<div slot="footer" class="d-flex justify-content-end gap-s">
		<ButtonV2
			variant="secondary"
			aria-label={$format('button_cancel', { default: 'Cancel' })}
			disabled={waitForResponse}
			on:click={() => (showClearDesignationModal = false)}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</ButtonV2>
		<ButtonV2
			aria-label={$format('button_confirm', { default: 'Confirm' })}
			loading={waitForResponse}
			disabled={waitForResponse}
			on:click={handleClearDesignationConfirm}
		>
			{$format('button_confirm', { default: 'Confirm' })}
		</ButtonV2>
	</div>
</Modal>
